import { useState, useEffect, useRef, useCallback } from 'react'
import { findIndex, sortBy, castArray, fill, find, set, get, clamp } from 'lodash'
import debounce from 'lodash/debounce'
import {
    THUMBNAIL_SIZE,
    PLACEHOLDER_IMAGE,
    SCALE_VALUES,
    IMAGE_STATE_ERROR,
    IMAGE_STATE_LOADED,
    IMAGE_STATE_NOT_LOADED,
    ROTATION_SAVE_DELAY,
} from '../../../Constants'
import {
    saveRotation,
    deletePhotos,
    updatePhotos,
} from '../../../Utils/SubmitUtil'
import { getImageObjects, extractImageUrlFromVariants } from '../../../fhg/utils/Utils'
import { MOVE_NEXT, MOVE_PREVIOUS, MOVE_FIRST, MOVE_LAST } from '../components/PhotoMoreMenu'
import { posthogCapture } from '../../../PostHogFunction'
import eventCapture from '../../../eventList'

const useEquipmentImageDetail = (props) => {
    const {
        evalItem,
        allowRotate: initialAllowRotate = true,
        allowZoom: initialAllowZoom = true,
        onRefresh,
        onZoom,
        onThumbnailsSelected,
    } = props

    // Refs
    const imageRef = useRef(null)
    const imageGridRef = useRef(null)
    const mainRef = useRef(null)
    const loadingRef = useRef(null)
    const isCancelLoadingRef = useRef(false)
    const useAnimationRef = useRef(true)

    // Initialize state from props
    const initialImages = getImageObjects(evalItem)
    
    // State
    const [selectedImage, setSelectedImage] = useState(initialImages[0])
    const [images, setImages] = useState(initialImages)
    const [imageWidth, setImageWidth] = useState('100%')
    const [imageHeight, setImageHeight] = useState('100%')
    const [isZoomed, setIsZoomed] = useState(get(props, 'location.state.isZoomed', false))
    const [scale, setScale] = useState(1)
    const [isScaledLocation, setIsScaledLocation] = useState(false)
    const [thumbColumns, setThumbColumns] = useState(4)
    const [showConfirmDelete, setShowConfirmDelete] = useState(false)
    const [selected, setSelected] = useState(fill([], false, 0, initialImages.length))
    const [showError, setShowError] = useState(false)
    const [errorValues, setErrorValues] = useState(null)
    const [deletePhoto, setDeletePhoto] = useState(null)
    const [showCategorySelect, setShowCategorySelect] = useState(false)
    const [refreshThumbnails, setRefreshThumbnails] = useState(null)
    const [refresh, setRefresh] = useState(null)

    // Computed values
    const allowRotateComputed = initialAllowRotate && selectedImage && 
        extractImageUrlFromVariants(selectedImage, 'original') !== PLACEHOLDER_IMAGE
    const allowZoomComputed = initialAllowZoom && selectedImage && 
        extractImageUrlFromVariants(selectedImage, 'original') !== PLACEHOLDER_IMAGE
    const thumbnailsSelected = selected.filter(item => item === true).length

    /**
     * Preload images sequentially
     */
    const preloadSequential = useCallback(async () => {
        const imageIds = images.map(image => image.image_id)

        for (let imageId of imageIds) {
            // Cancel loading loop if "isCancelLoading"
            if (isCancelLoadingRef.current) {
                return
            }
            try {
                await loadImage(imageId)
            } catch (e) {
                console.log(e)
            }
        }
    }, [images])

    /**
     * Load a single image from unloadedOriginals at the index
     */
    const loadImage = useCallback((imageId) => {
        if (imageId) {
            const image = find(images, { image_id: imageId })
            const src = extractImageUrlFromVariants(image, 'original')

            if (image.__state === IMAGE_STATE_NOT_LOADED) {
                if (src !== PLACEHOLDER_IMAGE) {
                    return new Promise((resolve) => {
                        let newImage = new Image()
                        newImage.src = src
                        newImage.onload = () => {
                            image.__state = IMAGE_STATE_LOADED
                            if (imageId === get(selectedImage, 'image_id')) {
                                setRefresh(Date.now())
                                handleResize()
                            }
                            resolve(image)
                        }
                        newImage.onerror = () => {
                            image.__state = IMAGE_STATE_ERROR
                            onError(image)()
                            resolve(image)
                        }
                    })
                } else {
                    image.__state = IMAGE_STATE_LOADED
                    setRefresh(Date.now())
                    handleResize()
                }
            }
        }
        return Promise.resolve()
    }, [images, selectedImage])

    /**
     * Preload images asynchronously
     */
    const preloadAsync = useCallback((imagesToLoad, path) => {
        const promiseArray = []

        for (const image of imagesToLoad) {
            if (path !== 'sizes.original' || image.__state === IMAGE_STATE_NOT_LOADED) {
                promiseArray.push(
                    new Promise((resolve) => {
                        const imageObject = new Image()

                        imageObject.onload = () => {
                            if (path === 'sizes.original') {
                                image.__state = IMAGE_STATE_LOADED
                            }
                            resolve()
                        }

                        imageObject.onerror = () => {
                            console.log('Could not load image - ' + imageObject.src)
                            if (path === 'sizes.original') {
                                image.__state = IMAGE_STATE_ERROR
                            }
                            resolve()
                        }

                        imageObject.src = get(image, path)
                    })
                )
            }
        }

        return Promise.all(promiseArray)
    }, [])

    /**
     * Find changed images between new and old image arrays
     */
    const findChangedImages = useCallback((newImages, oldImages) => {
        let hasNewOrUpdated = false

        for (const newImage of newImages) {
            const foundExisting = find(oldImages, {
                image_id: newImage.image_id,
            })
            if (foundExisting) {
                if (
                extractImageUrlFromVariants(newImage, 'original') ===
                extractImageUrlFromVariants(foundExisting, 'original')
            ) {
                    newImage.__state = foundExisting.__state
                    newImage.__rotation = foundExisting.__rotation
                } else {
                    hasNewOrUpdated = true
                }
            } else {
                hasNewOrUpdated = true
            }
        }
        return hasNewOrUpdated
    }, [])

    /**
     * Handle image resize
     */
    const handleResize = useCallback(() => {
        if (!mainRef.current || !imageRef.current) return

        const mainNode = mainRef.current
        const mainSlope = mainNode.clientHeight / mainNode.clientWidth
        const imageNode = imageRef.current
        const rotate = selectedImage.__rotation || 0
        let imageSlope = imageNode
            ? imageNode.naturalHeight / imageNode.naturalWidth
            : mainSlope

        if (rotate % 180 !== 0) {
            imageSlope = 1 / imageSlope
        }

        let newThumbColumns
        if (mainSlope < imageSlope) {
            newThumbColumns = 1
        } else {
            newThumbColumns = mainNode.clientWidth / THUMBNAIL_SIZE
        }

        setThumbColumns(newThumbColumns)
        updateImageSize(rotate)
    }, [selectedImage])

    // Debounced version of resize handler
    const handleDebounceResize = useCallback(
        debounce((e) => handleResize(e), 250),
        [handleResize]
    )

    /**
     * Update image size based on rotation
     */
    const updateImageSize = useCallback((rotate = 0) => {
        if (!imageGridRef.current) return { imageWidth, imageHeight }

        const isChangeOrientation = rotate % 180 !== 0
        const width = isChangeOrientation
            ? imageGridRef.current.clientHeight
            : imageGridRef.current.clientWidth
        const height = isChangeOrientation
            ? imageGridRef.current.clientWidth
            : imageGridRef.current.clientHeight

        setImageWidth(width)
        setImageHeight(height)
        return { imageWidth: width, imageHeight: height }
    }, [])

    /**
     * Handle error when loading an image
     */
    const onError = useCallback((image) => () => {
        image.__state = IMAGE_STATE_ERROR
        console.log('Could not load image - ' + get(image, 'sizes.original'))
    }, [])

    /**
     * Handle key press events for navigation
     */
    const handleKey = useCallback((event) => {
        if (!event.defaultPrevented && event.target.type !== 'textarea') {
            const selectedIndex = images.indexOf(selectedImage)
            if (
                (event.keyCode === 39 || event.keyCode === 40) &&
                event.target.type !== 'text'
            ) {
                event.preventDefault()
                selectImage((selectedIndex + 1) % images.length)()
            } else if (
                (event.keyCode === 38 || event.keyCode === 37) &&
                event.target.type !== 'text'
            ) {
                event.preventDefault()
                selectImage(
                    selectedIndex - 1 >= 0
                        ? selectedIndex - 1
                        : images.length - 1
                )()
            } else if (event.key === 'Escape') {
                if (isZoomed) {
                    if (scale !== 1) {
                        setScale(1)
                        setIsScaledLocation(false)
                    } else {
                        handleZoom(false)
                    }
                }
            }
        }
    }, [images, selectedImage, isZoomed, scale])

    /**
     * Select image at specific index
     */
    const selectImage = useCallback((imageOrIndex) => (event) => {
        if (!event || (!event.isDefaultPrevented() && !event.isPropagationStopped())) {
            const newSelectedImage = typeof imageOrIndex === 'number'
                ? images[imageOrIndex]
                : imageOrIndex
            
            setSelectedImage(newSelectedImage)
            setScale(1)
            setIsScaledLocation(false)
            updateImageSize(newSelectedImage && newSelectedImage.__rotation)
        }
    }, [images, updateImageSize])

    /**
     * Handle thumbnail selection
     */
    const handleThumbnailSelect = useCallback((newSelected) => {
        setSelected(newSelected)
        onThumbnailsSelected && onThumbnailsSelected(newSelected.some(Boolean))
    }, [onThumbnailsSelected])

    /**
     * Cancel selection of thumbnails
     */
    const handleCancelSelection = useCallback(() => {
        const newSelected = fill([], false, 0, images.length)
        setSelected(newSelected)
        onThumbnailsSelected && onThumbnailsSelected(false)
    }, [images.length, onThumbnailsSelected])

    /**
     * Rotate image
     */
    const onRotateClick = useCallback(() => {
        const newRotation = (selectedImage.__rotation || 0) - 90
        selectedImage.__rotation = newRotation

        updateImageSize(newRotation)
        handleSaveRotation()
    }, [selectedImage, updateImageSize])

    /**
     * Save rotation changes
     */
    const handleSaveRotation = useCallback(
        debounce(async () => {
            await saveRotation(selectedImage, selectedImage.__rotation)
            
            // Update images in state and props
            let image = find(images, { image_id: selectedImage.image_id })
            image.sizes = selectedImage.sizes
            
            // Update images in props
            image = find(evalItem.image_data, { image_id: selectedImage.image_id })
            if (image) {
                image.sizes = selectedImage.sizes
            }
            
            await preloadAsync([selectedImage], 'sizes.original')
            useAnimationRef.current = false
            
            updateImageSize()
            setRefreshThumbnails(Date.now())
        }, ROTATION_SAVE_DELAY),
        [selectedImage, images, evalItem, preloadAsync, updateImageSize]
    )

    /**
     * Toggle zoom
     */
    const handleZoom = useCallback((newIsZoomed) => {
        if (!isZoomed && newIsZoomed) {
            posthogCapture(eventCapture.ZOOM)
        }
        
        setIsZoomed(newIsZoomed)
        setScale(1)
        setIsScaledLocation(false)
        
        onZoom && onZoom(newIsZoomed)
        setTimeout(handleResize, 1)
    }, [isZoomed, onZoom, handleResize])

    /**
     * Toggle zoom on click
     */
    const onZoomClick = useCallback(() => {
        handleZoom(!isZoomed)
    }, [isZoomed, handleZoom])

    /**
     * Zoom in
     */
    const onZoomInClick = useCallback((event, newIsScaledLocation = false) => {
        const newScale = SCALE_VALUES.find((scaleValue) => scaleValue > scale) || 
            SCALE_VALUES[SCALE_VALUES.length - 1]
        
        setScale(newScale)
        setIsScaledLocation(newIsScaledLocation)
    }, [scale])

    /**
     * Zoom out
     */
    const onZoomOutClick = useCallback((event, newIsScaledLocation = false) => {
        const currentIndex = SCALE_VALUES.findIndex(
            (scaleValue, index, array) => 
                scaleValue < scale && (index === array.length - 1 || array[index + 1] >= scale)
        )
        
        const newScale = SCALE_VALUES[Math.max(currentIndex, 0)]
        setScale(newScale)
        setIsScaledLocation(newIsScaledLocation)
    }, [scale])

    /**
     * Reset zoom
     */
    const onZoomResetClick = useCallback(() => {
        setScale(1)
        setIsScaledLocation(false)
    }, [])

    /**
     * Handle click on image for zooming
     */
    const onClick = useCallback(({ altKey }) => {
        if (!altKey) {
            onZoomInClick(undefined, true)
        } else {
            onZoomOutClick(undefined, true)
        }
    }, [onZoomInClick, onZoomOutClick])

    /**
     * Handle mouse wheel for zooming
     */
    const onWheel = useCallback((event) => {
        if (isZoomed) {
            event.stopPropagation()
            event.preventDefault()

            const scrollDirection = -Math.sign(event.deltaY)
            let newScale = scale + scrollDirection * 0.04

            newScale = Math.max(
                SCALE_VALUES[0],
                Math.min(SCALE_VALUES[SCALE_VALUES.length - 1], newScale)
            )

            setScale(newScale)
            setIsScaledLocation(true)
        }
    }, [isZoomed, scale])

    /**
     * Handle confirming photo deletion
     */
    const handleConfirmDeletePhoto = useCallback((image) => () => {
        setShowConfirmDelete(true)
        setDeletePhoto(image)
    }, [])

    /**
     * Close deletion confirmation dialog
     */
    const handleCloseConfirmDeletePhoto = useCallback(() => {
        setShowConfirmDelete(false)
        setDeletePhoto(undefined)
    }, [])

    /**
     * Handle categorizing photos
     */
    const handleCategorizePhoto = useCallback(() => {
        setShowCategorySelect(true)
    }, [])

    /**
     * Close categorize photo dialog
     */
    const closeCategorizePhoto = useCallback(() => {
        setShowCategorySelect(false)
    }, [])

    /**
     * Get selected images
     */
    const getSelectedImages = useCallback((selectedArr, imagesArr) => {
        const selectedImages = []

        for (let i = 0; i < selectedArr.length; i++) {
            if (selectedArr[i]) {
                selectedImages.push(imagesArr[i])
            }
        }

        return selectedImages
    }, [])

    /**
     * Handle photo deletion
     */
    const handleDeletePhoto = useCallback((image) => async () => {
        const itemId = get(evalItem, 'item_id')
        const imageArray = image
            ? castArray(image)
            : getSelectedImages(selected, images)
        
        try {
            let stateImages = await deletePhotos(images, imageArray, itemId)
            posthogCapture(eventCapture.DELETE_IMAGE)

            set(evalItem, 'image_data', stateImages)

            // If the deleted photo was deleted get the index
            const selectedIndex = images.indexOf(selectedImage)

            // If the last photo is deleted, get the placeholder photo
            if (stateImages.length === 0) {
                stateImages = getImageObjects(evalItem)
            }

            console.log(
                'IMAGE ARRAY :',
                imageArray,
                'Images :',
                images,
                'Item ID :',
                itemId
            )

            // Set the list of images without deleted. Select the next image if the selected photo was deleted.
            setImages(stateImages)
            setShowConfirmDelete(false)
            setDeletePhoto(undefined)
            setSelected(fill([], false, 0, stateImages.length))
            
            onThumbnailsSelected && onThumbnailsSelected(false)
            selectImage(clamp(selectedIndex, 0, stateImages.length - 1))()
        } catch (error) {
            setShowError(true)
            setErrorValues(error)
        }
    }, [evalItem, images, selected, selectedImage, getSelectedImages, onThumbnailsSelected, selectImage])

    /**
     * Find images that don't have an order set and set the order
     */
    const updateMissingOrder = useCallback((imagesToUpdate, lastIndex) => {
        const updatePhotos = []

        for (let i = 0; i <= lastIndex; i++) {
            const image = imagesToUpdate[i]
            if (image.order === undefined || image.order !== i) {
                image.order = i
                updatePhotos.push(image)
            }
        }
        return updatePhotos
    }, [])

    /**
     * Add image to list if it doesn't exist
     */
    const addUniqueImage = useCallback((image, imagesArr) => {
        const foundIndex = findIndex(imagesArr, {
            original_filename: image.original_filename,
        })
        if (foundIndex < 0) {
            imagesArr.push(image)
        }
    }, [])

    /**
     * Move photo in the list
     */
    const handleMovePhoto = useCallback((image) => async (action) => {
        const itemId = get(evalItem, 'item.item_id')
        const index = findIndex(images, {
            original_filename: image.original_filename,
        })
        const lastIndex =
            action === MOVE_FIRST || action === MOVE_PREVIOUS
                ? index
                : action === MOVE_LAST
                    ? images.length - 1
                    : index + 1
        let updatedImages = updateMissingOrder(images, lastIndex)

        addUniqueImage(image, updatedImages)

        switch (action) {
            case MOVE_NEXT:
                image.order = index + 1
                images[index + 1].order = index
                addUniqueImage(images[index + 1], updatedImages)
                break
            case MOVE_PREVIOUS:
                image.order = index - 1
                images[index - 1].order = index
                addUniqueImage(images[index - 1], updatedImages)
                break
            case MOVE_FIRST:
                for (let i = 0; i < index; i++) {
                    images[i].order = i + 1
                    addUniqueImage(images[i], updatedImages)
                }
                image.order = 0
                break
            case MOVE_LAST:
                for (let i = index + 1; i < images.length; i++) {
                    images[i].order = i - 1
                    addUniqueImage(images[i], updatedImages)
                }
                image.order = images.length - 1
                break
            default:
                console.log('Photo move action ' + action + ' is unknown')
                break
        }
        try {
            await updatePhotos(updatedImages, itemId)
            const sortedImages = sortBy(images, 'order')
            set(evalItem, 'image_data', sortedImages)

            setImages(sortedImages)
            handleCancelSelection()
        } catch (error) {
            setShowError(true)
            setErrorValues(error)
        }
    }, [images, evalItem, updateMissingOrder, addUniqueImage, handleCancelSelection])

    /**
     * Sort photos by name
     */
    const handleSortPhotos = useCallback(async () => {
        const itemId = get(evalItem, 'item.item_id')

        const newImages = sortBy(images, ['original_filename', 'image_id'])
        let updatedImages = updateMissingOrder(newImages, newImages.length - 1)
        
        try {
            await updatePhotos(updatedImages, itemId)
            const sortedImages = sortBy(images, 'order')
            set(evalItem, 'image_data', sortedImages)

            setImages(sortedImages)
            handleCancelSelection()
        } catch (error) {
            setShowError(true)
            setErrorValues(error)
        }
    }, [images, evalItem, updateMissingOrder, handleCancelSelection])

    /**
     * Close error dialog
     */
    const handleErrorClose = useCallback(() => {
        setShowError(false)
    }, [])

    /**
     * Handle refresh
     */
    const handleRefresh = useCallback(() => {
        onRefresh && onRefresh(evalItem.item, true)
    }, [evalItem, onRefresh])

    /**
     * Handle photo recategorize refresh
     */
    const onPhotoRecategorizeRefresh = useCallback(() => {
        handleCancelSelection()
        onRefresh && onRefresh(evalItem.item, true)
    }, [evalItem, onRefresh, handleCancelSelection])

    // Add URL evaluation_id param on component mount
    useEffect(() => {
        const url = new URL(window.location)
        url.searchParams.set(
            'evaluation_id',
            evalItem?.item?.initial_eval
        )
        window.history.replaceState(null, '', url.toString())
    }, [evalItem])

    // Add event listeners
    useEffect(() => {
        window.addEventListener('resize', handleDebounceResize)
        document.addEventListener('keydown', handleKey, false)
        
        return () => {
            isCancelLoadingRef.current = true
            window.removeEventListener('resize', handleDebounceResize)
            document.removeEventListener('keydown', handleKey, false)
        }
    }, [handleDebounceResize, handleKey])

    // Preload images on component mount
    useEffect(() => {
        const loadImages = async () => {
            try {
                await preloadAsync(images, 'sizes.original')
                setTimeout(() => {
                    preloadSequential()
                }, 2)
            } catch (e) {
                console.log(e)
            }
        }
        
        loadImages()
    }, []) // Run only on mount

    // Handle props updates
    useEffect(() => {
        const newImages = getImageObjects(evalItem)
        const hasNewOrUpdates = findChangedImages(newImages, images)
        
        if (hasNewOrUpdates) {
            const newSelectedImage = find(newImages, {
                image_id: selectedImage.image_id
            }) || newImages[0]
            
            setSelectedImage(newSelectedImage)
            setImages(newImages)
            setImageWidth('100%')
            setImageHeight('100%')
            
            // Preload new images
            const loadNewImages = async () => {
                try {
                    await preloadAsync(newImages, 'sizes.original')
                } catch (e) {
                    console.log(e)
                }
            }
            loadNewImages()
        }
    }, [evalItem, findChangedImages, preloadAsync])

    // Reset state when allowRotate changes
    useEffect(() => {
        if (initialAllowRotate !== props.allowRotate) {
            const newImages = [...images]
            newImages.forEach(image => image.__rotation = 0)
            setImages(newImages)
            setImageWidth('100%')
            setImageHeight('100%')
        }
    }, [initialAllowRotate, props.allowRotate])

    // Handle image resize when needed
    useEffect(() => {
        handleResize()
    }, [handleResize])
    
    return {
        // State
        selectedImage,
        images,
        imageWidth,
        imageHeight,
        isZoomed,
        scale,
        isScaledLocation,
        thumbColumns,
        showConfirmDelete,
        selected,
        showCategorySelect,
        refreshThumbnails,
        showError,
        errorValues,
        deletePhoto,
        
        // Computed
        allowRotateComputed,
        allowZoomComputed,
        thumbnailsSelected,
        useAnimation: useAnimationRef.current,
        
        // Functions
        selectImage,
        onClick,
        onWheel,
        onZoomClick,
        onZoomInClick,
        onZoomOutClick,
        onZoomResetClick,
        onRotateClick,
        handleConfirmDeletePhoto,
        handleCloseConfirmDeletePhoto,
        handleDeletePhoto,
        handleCategorizePhoto,
        closeCategorizePhoto,
        handleThumbnailSelect,
        handleCancelSelection,
        handleMovePhoto,
        handleSortPhotos,
        handleErrorClose,
        handleRefresh,
        onPhotoRecategorizeRefresh,
        onError,
        getSelectedImages,
        
        // Refs
        imageRef,
        imageGridRef,
        mainRef
        ,
        // Expose immediate resize trigger for external layout changes
        triggerImmediateResize: handleResize
    }
}

export default useEquipmentImageDetail
